import React from "react";
import { useState } from "react";
import {
  X,
  FileText,
  User,
  FileImage,
  AlertTriangle,
  CheckCircle,
  Shield,
  Eye,
} from "lucide-react";
import { useForm } from "react-hook-form";
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";
import { ReactComponent as UserBoxIcon } from "../../assets/ic_user_box.svg";
import { ReactComponent as UserIcon } from "../../assets/ic_user.svg";
import { useVerifyCustomer } from "../../hooks/useVerification";
import { useCustomerDocuments } from "../../hooks/useCustomers";
import { useCreateCustomerEntry } from "../../hooks/useDailyEntries";
import { useCountry } from "../../hooks/useCountries";
import { Customer } from "../../api/services/cashExchange/verificationService";
import { Document } from "../../api/services/documents/documentService";
import toast from "react-hot-toast";
import { useIndividualState } from "../../hooks/useState";

interface VerifyCustomerFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  isLoading?: boolean;
}

interface VerifyFormData {
  playerId: string;
}

type TabType = "basicInfo" | "documents";

export const VerifyCustomerForm: React.FC<VerifyCustomerFormProps> = ({
  onCancel,
  onSuccess,
  onError,
  isLoading = false,
}) => {
  const [verifiedCustomer, setVerifiedCustomer] = useState<Customer | null>(
    null,
  );
  const [isVerified, setIsVerified] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("basicInfo");

  const verifyCustomerMutation = useVerifyCustomer();
  const createCustomerEntryMutation = useCreateCustomerEntry();
  const { data: customerDocuments } = useCustomerDocuments(
    verifiedCustomer?.id || "",
  );
  const { data: countryData } = useCountry(verifiedCustomer?.countryId || "");
  const { data: stateData } = useIndividualState(
    verifiedCustomer?.stateId || "",
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<VerifyFormData>();

  const watchPlayerId = watch("playerId");

  React.useEffect(() => {
    if (verifiedCustomer && verifiedCustomer.playerId !== watchPlayerId) {
      setIsVerified(false);
      setVerifiedCustomer(null);
      setActiveTab("basicInfo");
    }
  }, [watchPlayerId, verifiedCustomer]);

  const handleVerifyClick = async () => {
    const playerId = watchPlayerId;
    if (playerId?.trim()) {
      try {
        const response = await verifyCustomerMutation.mutateAsync(playerId);
        setVerifiedCustomer(response.data);
        setIsVerified(true);
        setActiveTab("basicInfo");
      } catch {
        setIsVerified(false);
        setVerifiedCustomer(null);
      }
    }
  };

  const onFormSubmit = () => {
    if (isVerified && verifiedCustomer) {
      createCustomerEntryMutation.mutate(verifiedCustomer.id, {
        onSuccess: () => {
          toast.success("Customer entry created successfully!");
          onSuccess?.();
        },
        onError: (error: Error) => {
          const errorMessage =
            error?.message || "Failed to create customer entry";
          toast.error(errorMessage);
          onError?.(errorMessage);
        },
      });
    }
  };

  const getCountryName = () => {
    return countryData?.name || "Unknown";
  };

  const getStateName = () => {
    return stateData?.name || "Unknown";
  };

  const getBanStatusInfo = (banStatus: string) => {
    switch (banStatus) {
      case "active":
        return {
          label: "Active",
          icon: <CheckCircle className="w-4 h-4" />,
          color: "text-green-400",
          bgColor: "bg-green-600/20",
          borderColor: "border-green-600",
        };
      case "temporary_ban":
        return {
          label: "Temporary Ban",
          icon: <AlertTriangle className="w-4 h-4" />,
          color: "text-yellow-400",
          bgColor: "bg-yellow-600/20",
          borderColor: "border-yellow-600",
        };
      case "permanent_ban":
        return {
          label: "Permanent Ban",
          icon: <X className="w-4 h-4" />,
          color: "text-red-400",
          bgColor: "bg-red-600/20",
          borderColor: "border-red-600",
        };
      case "under_monitor":
        return {
          label: "Under Monitor",
          icon: <Eye className="w-4 h-4" />,
          color: "text-blue-400",
          bgColor: "bg-blue-600/20",
          borderColor: "border-blue-600",
        };
      default:
        return {
          label: "Unknown",
          icon: <Shield className="w-4 h-4" />,
          color: "text-slate-400",
          bgColor: "bg-slate-600/20",
          borderColor: "border-slate-600",
        };
    }
  };

  const isDocumentExpired = (expireDate: string | null) => {
    if (!expireDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(expireDate);
    return expiryDate < today;
  };

  const getExpiredDocumentsCount = () => {
    if (!customerDocuments) return 0;
    return customerDocuments.filter((doc) => isDocumentExpired(doc.expireDate))
      .length;
  };

  const renderDocumentPreview = (document: Document, index: number) => {
    const isPdf = document.filePath.toLowerCase().endsWith(".pdf");
    const fullUrl = `${process.env.REACT_APP_API_BASE_URL}/${document.filePath}`;
    const isExpired = isDocumentExpired(document.expireDate);

    return (
      <div
        key={document.id}
        className={`border rounded-lg p-4 ${isExpired ? "border-red-600 bg-red-600/10" : "border-slate-600"}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-slate-400" />
          <h4 className="text-white font-medium">
            {document.isRequired ? "Required Document" : "Optional Document"}
          </h4>
          <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
            {document.docsType.replace("_", " ").toUpperCase()}
          </span>
          {isExpired && (
            <span className="text-xs bg-red-600 text-red-100 px-2 py-1 rounded flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              EXPIRED
            </span>
          )}
        </div>

        {/* Add expiry warning at the top if expired */}
        {isExpired && (
          <div className="bg-red-600/20 border border-red-600 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Document Expired</span>
            </div>
            <p className="text-red-300 text-xs mt-1">
              This document expired on{" "}
              {new Date(document.expireDate!).toLocaleDateString()}. Please
              request customer to update their document.
            </p>
          </div>
        )}

        <div className="bg-slate-700 rounded-lg p-4">
          {isPdf ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">PDF Document</span>
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-sm underline"
                >
                  View Full Document
                </a>
              </div>

              {/* Larger PDF Preview using iframe */}
              <div className="border border-slate-600 rounded">
                <iframe
                  src={`${fullUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full h-64 rounded"
                  title={`Document ${index + 1}`}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <span className="text-slate-300 text-sm">Image Document</span>
              <img
                src={fullUrl}
                alt={`Document ${index + 1}`}
                className="w-full h-64 object-contain rounded border border-slate-600"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.src =
                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTkgMTJIMTVNOSAxNkgxNE0xNyAyMUg3QTIgMiAwIDAgMSA1IDE5VjVBMiAyIDAgMCAxIDcgM0gxMi41ODZBMSAxIDAgMCAxIDEzLjI5MyAzLjI5M0wxOC43MDcgOC43MDdBMSAxIDAgMCAxIDE5IDkuNDE0VjE5QTIgMiAwIDAgMSAxNyAyMVoiIHN0cm9rZT0iIzk0YTNiOCIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+";
                }}
              />
            </div>
          )}

          {document.expireDate && (
            <div className="mt-3 text-xs text-slate-400">
              Expires: {new Date(document.expireDate).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBasicInfoTab = () => {
    const banStatusInfo = getBanStatusInfo(verifiedCustomer?.banStatus || "");

    return (
      <div className="bg-slate-800 border border-green-600 rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-6">
          Customer Information
        </h3>
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            {verifiedCustomer?.photo ? (
              <>
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}/${verifiedCustomer.photo}`}
                  alt={verifiedCustomer.name}
                  className="w-24 h-24 rounded-full object-cover border-2 border-green-600"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    const fallback =
                      target.nextElementSibling as HTMLElement | null;
                    target.style.display = "none";
                    if (fallback) {
                      fallback.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="w-24 h-24 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600"
                  style={{ display: "none" }}
                >
                  <UserIcon className="w-12 h-12 text-slate-300" />
                </div>
              </>
            ) : (
              <div className="w-24 h-24 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600">
                <UserIcon className="w-12 h-12 text-slate-300" />
              </div>
            )}
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <h4 className="text-white font-medium text-xl mb-2">
                {verifiedCustomer?.name}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-400 text-sm font-medium">
                      Player ID
                    </p>
                    <p className="text-slate-200">
                      {verifiedCustomer?.playerId}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm font-medium">
                      Country
                    </p>
                    <p className="text-slate-200">
                      {verifiedCustomer?.countryId ? getCountryName() : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm font-medium">State</p>
                    <p className="text-slate-200">
                      {verifiedCustomer?.stateId ? getStateName() : "N/A"}
                    </p>
                  </div>
                  {verifiedCustomer?.phone && (
                    <div>
                      <p className="text-slate-400 text-sm font-medium">
                        Phone
                      </p>
                      <p className="text-slate-200">{verifiedCustomer.phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-400 text-sm font-medium">Email</p>
                    <p className="text-slate-200">{verifiedCustomer?.email}</p>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-400 text-sm font-medium mb-2">
                      Status
                    </p>
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border ${banStatusInfo.bgColor} ${banStatusInfo.borderColor}`}
                    >
                      <span className={banStatusInfo.color}>
                        {banStatusInfo.icon}
                      </span>
                      <span
                        className={`text-sm font-medium ${banStatusInfo.color}`}
                      >
                        {banStatusInfo.label}
                      </span>
                    </div>
                  </div>

                  {verifiedCustomer?.banStatus === "temporary_ban" &&
                    verifiedCustomer?.banEndDate && (
                      <div>
                        <p className="text-slate-400 text-sm font-medium">
                          Ban End Date
                        </p>
                        <p className="text-yellow-400 font-medium">
                          {new Date(
                            verifiedCustomer.banEndDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}

                  {verifiedCustomer?.banRemarks && (
                    <div>
                      <p className="text-slate-400 text-sm font-medium">
                        Remarks
                      </p>
                      <p className="text-slate-200 text-sm">
                        {verifiedCustomer.banRemarks}
                      </p>
                    </div>
                  )}

                  {verifiedCustomer?.customerCardNumber && (
                    <div>
                      <p className="text-slate-400 text-sm font-medium">
                        Card Number
                      </p>
                      <p className="text-slate-200">
                        {verifiedCustomer.customerCardNumber}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-slate-400 text-sm font-medium">
                      Customer Type
                    </p>
                    <p className="text-slate-200 capitalize">
                      {verifiedCustomer?.customerType}
                    </p>
                  </div>

                  {verifiedCustomer?.dateOfBirth && (
                    <div>
                      <p className="text-slate-400 text-sm font-medium">
                        Date of Birth
                      </p>
                      <p className="text-slate-200">
                        {new Date(
                          verifiedCustomer.dateOfBirth,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentsTab = () => {
    const expiredCount = getExpiredDocumentsCount();

    return (
      <div className="space-y-6">
        {/* Add expired documents summary */}
        {expiredCount > 0 && (
          <div className="bg-red-600/20 border border-red-600 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-400 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <h4 className="font-medium">Document Expiry Alert</h4>
            </div>
            <p className="text-red-300 text-sm">
              {expiredCount} document{expiredCount > 1 ? "s have" : " has"}{" "}
              expired. Please request the customer to update their expired
              document{expiredCount > 1 ? "s" : ""}.
            </p>
          </div>
        )}

        {customerDocuments && customerDocuments.length > 0 ? (
          <div className="space-y-6">
            {customerDocuments.map((document, index) =>
              renderDocumentPreview(document, index),
            )}
          </div>
        ) : (
          <div className="bg-slate-800 border border-yellow-600 rounded-lg p-8">
            <div className="text-center">
              <FileImage className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <p className="text-yellow-400 text-lg">
                No documents found for this customer.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 shadow-2xl border border-slate-600 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 className="text-xl font-semibold text-yellow-400">
            Verify Customer
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            disabled={isLoading || createCustomerEntryMutation.isPending}
          >
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <p className="text-slate-400 text-sm mb-6">
            Please enter the customer Casino ID to verify their identity.
          </p>

          <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Casino Player ID
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <CustomInput
                    type="text"
                    placeholder="Enter player ID"
                    register={register}
                    name="playerId"
                    validation={{ required: "Player ID is required" }}
                    error={errors.playerId}
                    disabled={isLoading}
                    icon={<UserBoxIcon className="w-5 h-5 text-slate-400" />}
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={handleVerifyClick}
                  disabled={
                    verifyCustomerMutation.isPending ||
                    isLoading ||
                    !watchPlayerId?.trim()
                  }
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    isVerified
                      ? "bg-green-600 text-white"
                      : "bg-slate-600 hover:bg-slate-500 text-white disabled:bg-slate-700 disabled:text-slate-400"
                  }`}
                >
                  {verifyCustomerMutation.isPending
                    ? "Verifying..."
                    : isVerified
                      ? "Verified"
                      : "Verify"}
                </button>
              </div>
            </div>

            {/* Customer Information with Tabs */}
            {isVerified && verifiedCustomer && (
              <div className="space-y-6">
                {/* Tab Navigation */}
                <div className="border-b border-slate-600">
                  <nav className="flex space-x-8">
                    <button
                      type="button"
                      onClick={() => setActiveTab("basicInfo")}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === "basicInfo"
                          ? "border-yellow-400 text-yellow-400"
                          : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Basic Information
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("documents")}
                      className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === "documents"
                          ? "border-yellow-400 text-yellow-400"
                          : "border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Documents
                        {customerDocuments && customerDocuments.length > 0 && (
                          <span className="bg-slate-600 text-slate-300 text-xs px-2 py-1 rounded-full">
                            {customerDocuments.length}
                          </span>
                        )}
                        {getExpiredDocumentsCount() > 0 && (
                          <span className="bg-red-600 text-red-100 text-xs px-2 py-1 rounded-full ml-1">
                            {getExpiredDocumentsCount()} Expired
                          </span>
                        )}
                      </div>
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="min-h-[300px]">
                  {activeTab === "basicInfo" && renderBasicInfoTab()}
                  {activeTab === "documents" && renderDocumentsTab()}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>

              <PrimaryButton
                type="submit"
                disabled={
                  isLoading ||
                  !isVerified ||
                  createCustomerEntryMutation.isPending
                }
                className="flex items-center gap-2"
              >
                {isLoading || createCustomerEntryMutation.isPending
                  ? "Creating Entry..."
                  : "Proceed"}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
